import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useAuth } from "./contexts/AuthContext";

const NotFound = lazy(() => import('pages/NotFound'));
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const JobEntryForm = lazy(() => import('./pages/job-entry-form'));
const ResellerDashboard = lazy(() => import('./pages/reseller-dashboard'));
const Login = lazy(() => import('./pages/login'));
const ClientPortal = lazy(() => import('./pages/client-portal'));
const QRCodeManagement = lazy(() => import('./pages/qr-code-management'));
const PublicQRVisitorPage = lazy(() => import('./pages/public-qr-visitor-page'));
const EnhancedProductTracking = lazy(() => import('./pages/enhanced-product-tracking'));
const ComprehensiveNotificationCenter = lazy(() => import('./pages/comprehensive-notification-center'));
const ServiceRequestWorkflow = lazy(() => import('./pages/service-request-workflow'));
const AdminProductCatalog = lazy(() => import('./pages/admin-product-catalog'));
const ResellerClientDatabase = lazy(() => import('./pages/reseller-client-database'));
const PublicQRFloorInformation = lazy(() => import('./pages/public-qr-floor-information'));
const PerformanceAnalyticsDashboard = lazy(() => import('./pages/performance-analytics-dashboard'));
const ResellerManagement = lazy(() => import('./pages/reseller-management'));
const AdminReports = lazy(() => import('./pages/admin-reports'));
const ResellerReports = lazy(() => import('./pages/reseller-reports'));
const QRScanPage = lazy(() => import('./pages/qr-scan'));
const ClientDetailsPage = lazy(() => import('./pages/client-details'));
const ClientNotificationCenter = lazy(() => import('./pages/client-notification-center'));
const ResellerNotificationCenter = lazy(() => import('./pages/reseller-notification-center'));
const JobHistory = lazy(() => import('./pages/job-history'));
const AdminGlobalClientDatabase = lazy(() => import('./pages/admin-global-client-database'));
const InternationalReferenceList = lazy(() => import('./pages/international-reference-list'));
const WarrantyCodeStock = lazy(() => import('./pages/warranty-code-stock'));
const AgencyDashboard = lazy(() => import('./pages/agency-dashboard'));
const AgencyQRCodes = lazy(() => import('./pages/agency-qr-codes'));
const InstallerWarrantyInventory = lazy(() => import('./pages/installer-warranty-inventory'));
const RegionalPartnerWarrantyInventory = lazy(() => import('./pages/regional-partner-warranty-inventory'));
const ManufacturerPortfolio = lazy(() => import('./pages/manufacturer-portfolio'));
const RegionalPartnerAssignment = lazy(() => import('./pages/regional-partner-assignment'));
const JobListView = lazy(() => import('./pages/job-list-view'));
const InstallerJobList = lazy(() => import('./pages/installer-job-list'));
const InstallerJobHistory = lazy(() => import('./pages/installer-job-history'));
const PhotoVisibilityManager = lazy(() => import('./pages/photo-visibility-manager'));
const RegionalPartnerDashboard = lazy(() => import('./pages/regional-partner-dashboard'));
const AdminMetricsDashboard = lazy(() => import('./pages/admin-metrics-dashboard'));
const JobLifecycle = lazy(() => import('./pages/job-lifecycle'));
const AdvancedReports = lazy(() => import('./pages/advanced-reports'));
const WarrantyPortal = lazy(() => import('./pages/warranty-portal'));
const InstallerJobChecklist = lazy(() => import('./pages/installer-job-checklist'));
const RegionalPartnerJobList = lazy(() => import('./pages/regional-partner-job-list'));
const RegionalPartnerJobHistory = lazy(() => import('./pages/regional-partner-job-history'));
const RegionalPartnerJobLifecycle = lazy(() => import('./pages/regional-partner-job-lifecycle'));
const RegionalPartnerNotifications = lazy(() => import('./pages/regional-partner-notifications'));
const RegionalPartnerReports = lazy(() => import('./pages/regional-partner-reports'));
const RegionalPartnerAdvancedReports = lazy(() => import('./pages/regional-partner-advanced-reports'));
const TeamKPIDashboard = lazy(() => import('./pages/team-kpi-dashboard'));
const AdminExportCenter = lazy(() => import('./pages/admin-export-center'));
const RegionalPartnerClientDatabase = lazy(() => import('./pages/regional-partner-client-database'));
const TerritoryOfflineAccess = lazy(() => import('./pages/territory-offline-access'));
const FieldMap = lazy(() => import('./pages/field-map'));
const AdminJobList = lazy(() => import('./pages/admin-job-list'));
const AdminSMTPSettings = lazy(() => import('./pages/admin-smtp-settings'));
const EmailTemplateManager = lazy(() => import('./pages/email-template-manager'));
const EmailAutomationRules = lazy(() => import('./pages/email-automation-rules'));
const AdminAuditLog = lazy(() => import('./pages/admin-audit-log'));
const ClientMyFloors = lazy(() => import('./pages/client-my-floors'));
const ClientFloorDetail = lazy(() => import('./pages/client-floor-detail'));
const ClientWarrantyCodes = lazy(() => import('./pages/client-warranty-codes'));
const ClientServiceRequests = lazy(() => import('./pages/client-service-requests'));
const InstallerDashboard = lazy(() => import('./pages/installer-dashboard'));
const InstallerClientDatabase = lazy(() => import('./pages/installer-client-database'));
const InstallerNotifications = lazy(() => import('./pages/installer-notifications'));
const InstallerServiceRequests = lazy(() => import('./pages/installer-service-requests'));
const MaintenanceScheduler = lazy(() => import('./pages/maintenance-scheduler'));
const ForcePasswordChange = lazy(() => import('./pages/force-password-change'));
const AdminInstallerManagement = lazy(() => import('./pages/admin-installer-management'));
const RegionalPartnerInstallerManagement = lazy(() => import('./pages/regional-partner-installer-management'));
const RPJobEntryForm = lazy(() => import('./pages/rp-job-entry-form'));
const RPOverview = lazy(() => import('./pages/rp-overview'));
const AdminTerritoryManagement = lazy(() => import('./pages/admin-territory-management'));
const UserProfile = lazy(() => import('./pages/user-profile'));
const AdminAgencyManagement = lazy(() => import('./pages/admin-agency-management'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const ResetPassword = lazy(() => import('./pages/reset-password'));
const AdminDataIntegrity = lazy(() => import('./pages/admin-data-integrity'));
const AgencyPartnerDistribution = lazy(() => import('./pages/agency-partner-distribution'));
const UserManual = lazy(() => import('./pages/user-manual'));
const ContactMyFloorID = lazy(() => import('./pages/contact-myfloorid'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfService = lazy(() => import('./pages/terms-of-service'));

// ── ProtectedRoute: redirects unauthenticated users to /login ─────────────────
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      }>
      <RouterRoutes>
        {/* ── Public routes ── */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/public-qr-visitor-page" element={<PublicQRVisitorPage />} />
        <Route path="/public-qr-floor-information" element={<PublicQRFloorInformation />} />
        <Route path="/qr-scan" element={<QRScanPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        {/* ── Authenticated routes ── */}
        <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin-job-list" element={<ProtectedRoute><AdminJobList /></ProtectedRoute>} />
        <Route path="/job-entry-form" element={<ProtectedRoute><JobEntryForm /></ProtectedRoute>} />
        <Route path="/reseller-dashboard" element={<ProtectedRoute><ResellerDashboard /></ProtectedRoute>} />
        <Route path="/installer-dashboard" element={<ProtectedRoute><InstallerDashboard /></ProtectedRoute>} />
        <Route path="/installer-client-database" element={<ProtectedRoute><InstallerClientDatabase /></ProtectedRoute>} />
        <Route path="/installer-notifications" element={<ProtectedRoute><InstallerNotifications /></ProtectedRoute>} />
        <Route path="/installer-service-requests" element={<ProtectedRoute><InstallerServiceRequests /></ProtectedRoute>} />
        <Route path="/maintenance-scheduler" element={<ProtectedRoute><MaintenanceScheduler /></ProtectedRoute>} />
        <Route path="/client-portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
        <Route path="/qr-code-management" element={<ProtectedRoute><QRCodeManagement /></ProtectedRoute>} />
        <Route path="/enhanced-product-tracking" element={<ProtectedRoute><EnhancedProductTracking /></ProtectedRoute>} />
        <Route path="/comprehensive-notification-center" element={<ProtectedRoute><ComprehensiveNotificationCenter /></ProtectedRoute>} />
        <Route path="/service-request-workflow" element={<ProtectedRoute><ServiceRequestWorkflow /></ProtectedRoute>} />
        <Route path="/admin-product-catalog" element={<ProtectedRoute><AdminProductCatalog /></ProtectedRoute>} />
        <Route path="/reseller-client-database" element={<ProtectedRoute><ResellerClientDatabase /></ProtectedRoute>} />
        <Route path="/performance-analytics-dashboard" element={<ProtectedRoute><PerformanceAnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/reseller-management" element={<ProtectedRoute><ResellerManagement /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
        <Route path="/admin-reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
        <Route path="/reseller-reports" element={<ProtectedRoute><ResellerReports /></ProtectedRoute>} />
        <Route path="/client-details/:clientId" element={<ProtectedRoute><ClientDetailsPage /></ProtectedRoute>} />
        <Route path="/client-notification-center" element={<ProtectedRoute><ClientNotificationCenter /></ProtectedRoute>} />
        <Route path="/reseller-notification-center" element={<ProtectedRoute><ResellerNotificationCenter /></ProtectedRoute>} />
        <Route path="/job-history" element={<ProtectedRoute><JobHistory /></ProtectedRoute>} />
        <Route path="/installer-job-list" element={<ProtectedRoute><InstallerJobList /></ProtectedRoute>} />
        <Route path="/installer-job-history" element={<ProtectedRoute><InstallerJobHistory /></ProtectedRoute>} />
        <Route path="/admin-global-client-database" element={<ProtectedRoute><AdminGlobalClientDatabase /></ProtectedRoute>} />
        <Route path="/international-reference-list" element={<ProtectedRoute><InternationalReferenceList /></ProtectedRoute>} />
        <Route path="/warranty-code-stock" element={<ProtectedRoute><WarrantyCodeStock /></ProtectedRoute>} />
        <Route path="/agency-dashboard" element={<ProtectedRoute><AgencyDashboard /></ProtectedRoute>} />
        <Route path="/agency-qr-codes" element={<ProtectedRoute><AgencyQRCodes /></ProtectedRoute>} />
        <Route path="/installer-warranty-inventory" element={<ProtectedRoute><InstallerWarrantyInventory /></ProtectedRoute>} />
        <Route path="/regional-partner-warranty-inventory" element={<ProtectedRoute><RegionalPartnerWarrantyInventory /></ProtectedRoute>} />
        <Route path="/manufacturer-portfolio" element={<ProtectedRoute><ManufacturerPortfolio /></ProtectedRoute>} />
        <Route path="/regional-partner-assignment" element={<ProtectedRoute><RegionalPartnerAssignment /></ProtectedRoute>} />
        <Route path="/job-list-view" element={<ProtectedRoute><JobListView /></ProtectedRoute>} />
        <Route path="/photo-visibility-manager" element={<ProtectedRoute><PhotoVisibilityManager /></ProtectedRoute>} />
        <Route path="/regional-partner-dashboard" element={<ProtectedRoute><RegionalPartnerDashboard /></ProtectedRoute>} />
        <Route path="/regional-partner-inventory" element={<Navigate to="/regional-partner-warranty-inventory" replace />} />
        <Route path="/admin-metrics-dashboard" element={<ProtectedRoute><AdminMetricsDashboard /></ProtectedRoute>} />
        <Route path="/job-lifecycle" element={<ProtectedRoute><JobLifecycle /></ProtectedRoute>} />
        <Route path="/advanced-reports" element={<ProtectedRoute><AdvancedReports /></ProtectedRoute>} />
        <Route path="/warranty-portal" element={<ProtectedRoute><WarrantyPortal /></ProtectedRoute>} />
        <Route path="/installer-job-checklist" element={<ProtectedRoute><InstallerJobChecklist /></ProtectedRoute>} />
        <Route path="/regional-partner-job-list" element={<ProtectedRoute><RegionalPartnerJobList /></ProtectedRoute>} />
        <Route path="/regional-partner-job-history" element={<ProtectedRoute><RegionalPartnerJobHistory /></ProtectedRoute>} />
        <Route path="/regional-partner-job-lifecycle" element={<ProtectedRoute><RegionalPartnerJobLifecycle /></ProtectedRoute>} />
        <Route path="/regional-partner-notifications" element={<ProtectedRoute><RegionalPartnerNotifications /></ProtectedRoute>} />
        <Route path="/regional-partner-reports" element={<ProtectedRoute><RegionalPartnerReports /></ProtectedRoute>} />
        <Route path="/regional-partner-advanced-reports" element={<ProtectedRoute><RegionalPartnerAdvancedReports /></ProtectedRoute>} />
        <Route path="/team-kpi-dashboard" element={<ProtectedRoute><TeamKPIDashboard /></ProtectedRoute>} />
        <Route path="/admin-export-center" element={<ProtectedRoute><AdminExportCenter /></ProtectedRoute>} />
        <Route path="/regional-partner-client-database" element={<ProtectedRoute><RegionalPartnerClientDatabase /></ProtectedRoute>} />
        <Route path="/territory-offline-access" element={<ProtectedRoute><TerritoryOfflineAccess /></ProtectedRoute>} />
        <Route path="/field-map" element={<ProtectedRoute><FieldMap /></ProtectedRoute>} />
        <Route path="/admin-smtp-settings" element={<ProtectedRoute><AdminSMTPSettings /></ProtectedRoute>} />
        <Route path="/email-template-manager" element={<ProtectedRoute><EmailTemplateManager /></ProtectedRoute>} />
        <Route path="/email-automation-rules" element={<ProtectedRoute><EmailAutomationRules /></ProtectedRoute>} />
        <Route path="/admin-audit-log" element={<ProtectedRoute><AdminAuditLog /></ProtectedRoute>} />
        <Route path="/client-my-floors" element={<ProtectedRoute><ClientMyFloors /></ProtectedRoute>} />
        <Route path="/client-floor-detail/:floorId" element={<ProtectedRoute><ClientFloorDetail /></ProtectedRoute>} />
        <Route path="/client-warranty-codes" element={<ProtectedRoute><ClientWarrantyCodes /></ProtectedRoute>} />
        <Route path="/client-service-requests" element={<ProtectedRoute><ClientServiceRequests /></ProtectedRoute>} />
        <Route path="/force-password-change" element={<ProtectedRoute><ForcePasswordChange /></ProtectedRoute>} />
        <Route path="/admin-installer-management" element={<ProtectedRoute><AdminInstallerManagement /></ProtectedRoute>} />
        <Route path="/regional-partner-installer-management" element={<ProtectedRoute><RegionalPartnerInstallerManagement /></ProtectedRoute>} />
        <Route path="/rp-job-entry-form" element={<ProtectedRoute><RPJobEntryForm /></ProtectedRoute>} />
        <Route path="/rp-overview" element={<ProtectedRoute><RPOverview /></ProtectedRoute>} />
        <Route path="/admin-territory-management" element={<ProtectedRoute><AdminTerritoryManagement /></ProtectedRoute>} />
        <Route path="/user-profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
        <Route path="/admin-agency-management" element={<ProtectedRoute><AdminAgencyManagement /></ProtectedRoute>} />
        <Route path="/admin-data-integrity" element={<ProtectedRoute><AdminDataIntegrity /></ProtectedRoute>} />
        <Route path="/agency-partner-distribution" element={<ProtectedRoute><AgencyPartnerDistribution /></ProtectedRoute>} />
        <Route path="/user-manual" element={<ProtectedRoute><UserManual /></ProtectedRoute>} />
        <Route path="/contact-myfloorid" element={<ProtectedRoute><ContactMyFloorID /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
